import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Paper,
  Typography,
} from '@material-ui/core';
import { TrendingUp } from '@material-ui/icons';
import { Chart } from "react-google-charts";

const useStyles = makeStyles((theme) => ({
  timelineContainer: {
    borderRadius: 16,
    background: 'linear-gradient(135deg, #0055A2 0%, #428BC9 100%)',
    color: 'white',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    boxShadow: '0 8px 32px rgba(0, 85, 162, 0.3)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -50,
      right: -50,
      width: 200,
      height: 200,
      background: 'rgba(255,255,255,0.05)',
      borderRadius: '50%',
    }
  },
  iconBox: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: 'rgba(255,255,255,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12
  },
  chartContainer: {
    background: 'rgba(255,255,255,0.95)',
    borderRadius: 12,
    padding: 16,
    height: 320,
    marginBottom: 16
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 16,
    background: 'rgba(255,255,255,0.1)',
    borderRadius: 12,
    padding: 16
  },
  summaryBox: {
    textAlign: 'center'
  },
  summaryIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    background: 'rgba(255,255,255,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 8px'
  }
}));

const TrainingProgressTimeline = ({ 
  trainingAdminData, 
  trainingData, 
  getTranslation, 
  preferredLanguage = 'english' 
}) => {
  const classes = useStyles();

  // Generate timeline chart data with module breakdown
  const timelineChartData = useMemo(() => {
    if (!trainingAdminData?.getUserLevelTrainingStats || !trainingData?.trainingItems) return null;

    const now = new Date();
    const sixMonthsAgo = new Date(now.getTime() - (180 * 24 * 60 * 60 * 1000));
    
    // Convert training admin data to array format
    const userStats = trainingAdminData.getUserLevelTrainingStats && 
      typeof trainingAdminData.getUserLevelTrainingStats === 'object' ? 
      Object.values(trainingAdminData.getUserLevelTrainingStats).flat() : [];

    // Collect all completions with module details
    const allCompletions = [];
    const moduleSet = new Set();
    
    userStats.forEach(user => {
      if (user.trainingCoursesCompleted && Array.isArray(user.trainingCoursesCompleted)) {
        user.trainingCoursesCompleted.forEach(course => {
          if (course.completedDate && course.trainingModuleId) {
            const completionDate = new Date(course.completedDate);
            if (completionDate >= sixMonthsAgo) {
              // Get module title from training data
              const moduleItem = trainingData.trainingItems.find(
                item => item.modId === course.trainingModuleId
              );
              
              let moduleTitle = 'Unknown Module';
              if (moduleItem?.title) {
                // Truncate very long titles for legend display but keep them readable
                moduleTitle = moduleItem.title.length > 35 
                  ? moduleItem.title.substring(0, 32) + '...' 
                  : moduleItem.title;
              }
              
              allCompletions.push({
                date: completionDate,
                moduleId: course.trainingModuleId,
                moduleTitle: moduleTitle
              });
              
              moduleSet.add(moduleTitle);
            }
          }
        });
      }
    });

    if (allCompletions.length === 0) return null;

    // Create module columns sorted by popularity
    const modules = Array.from(moduleSet);
    const modulePopularity = {};
    allCompletions.forEach(completion => {
      modulePopularity[completion.moduleTitle] = (modulePopularity[completion.moduleTitle] || 0) + 1;
    });
    const sortedModules = modules.sort((a, b) => (modulePopularity[b] || 0) - (modulePopularity[a] || 0))
      .slice(0, 8); // Limit to top 8 modules for better legend readability

    // Group completions by month and module
    const monthlyModuleData = new Map();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      
      const moduleData = {};
      sortedModules.forEach(module => {
        moduleData[module] = 0;
      });
      
      monthlyModuleData.set(monthName, moduleData);
    }
    
    // Fill in actual completion data
    allCompletions.forEach(completion => {
      const date = new Date(completion.date.getFullYear(), completion.date.getMonth(), 1);
      const monthName = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      
      if (monthlyModuleData.has(monthName)) {
        const moduleData = monthlyModuleData.get(monthName);
        if (moduleData[completion.moduleTitle] !== undefined) {
          moduleData[completion.moduleTitle]++;
        }
      }
    });

    // Build chart data with module columns
    const chartData = [['Month', ...sortedModules]];
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      const moduleData = monthlyModuleData.get(monthName);
      const row = [monthName, ...sortedModules.map(module => moduleData[module] || 0)];
      chartData.push(row);
    }

    return chartData;
  }, [trainingAdminData, trainingData]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!timelineChartData) return { totalCompletions: 0, monthlyAverage: 0, peakMonth: 0, activeModules: 0 };

    const dataRows = timelineChartData.slice(1); // Skip header row
    const totalCompletions = dataRows.reduce((sum, row) => {
      return sum + row.slice(1).reduce((rowSum, value) => rowSum + value, 0);
    }, 0);

    const monthlyAverage = Math.round(totalCompletions / 6);
    const peakMonth = Math.max(...dataRows.map(row => 
      row.slice(1).reduce((rowSum, value) => rowSum + value, 0)
    ));
    const activeModules = timelineChartData[0].length - 1; // Subtract 'Month' column

    return { totalCompletions, monthlyAverage, peakMonth, activeModules };
  }, [timelineChartData]);

  if (!timelineChartData) {
    return null;
  }

  return (
    <Paper className={classes.timelineContainer}>
      <Box display="flex" alignItems="center" marginBottom={3}>
        <Box className={classes.iconBox}>
          <TrendingUp style={{ color: 'white', fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h5" style={{ fontWeight: 700, color: 'white', marginBottom: 4 }}>
            📈 {getTranslation('Training Progress Timeline', preferredLanguage)}
          </Typography>
          <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
            6-month view • Course completions & module engagement trends
          </Typography>
        </Box>
      </Box>

      <Box className={classes.chartContainer}>
        <Chart
          chartType="AreaChart"
          data={timelineChartData}
          options={{
            backgroundColor: 'transparent',
            chartArea: { left: 60, top: 30, width: '80%', height: '70%' },
            colors: ['#0055A2', '#428BC9', '#FFD700', '#FF6B9D', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'],
            isStacked: true,
            curveType: 'function',
            lineWidth: 0,
            areaOpacity: 0.8,
            legend: { 
              position: 'right',
              alignment: 'start',
              textStyle: { color: '#333', fontSize: 10, fontName: 'Montserrat' },
              maxLines: 8
            },
            hAxis: {
              textStyle: { color: '#666', fontSize: 11 },
              gridlines: { color: '#f0f0f0', count: 6 },
              format: 'MMM yyyy'
            },
            vAxis: {
              textStyle: { color: '#666', fontSize: 11 },
              gridlines: { color: '#f0f0f0' },
              minValue: 0,
              title: 'Completions',
              titleTextStyle: { color: '#333', fontSize: 12 }
            },
            animation: {
              startup: true,
              duration: 1200,
              easing: 'out',
            },
            tooltip: {
              isHtml: true,
              textStyle: { color: '#333', fontSize: 12 }
            },
            focusTarget: 'category'
          }}
          width="100%"
          height="100%"
          loader={<div style={{ color: '#666' }}>Loading training timeline...</div>}
        />
      </Box>

      {/* Enhanced Summary Stats */}
      <Box className={classes.summaryGrid}>
        <Box className={classes.summaryBox}>
          <Box className={classes.summaryIcon}>
            <Typography style={{ fontSize: '1.2rem' }}>🏆</Typography>
          </Box>
          <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
            {getTranslation('Total Completions', preferredLanguage)}
          </Typography>
          <Typography variant="h5" style={{ fontWeight: 700, color: 'white' }}>
            {summaryStats.totalCompletions}
          </Typography>
        </Box>
        
        <Box className={classes.summaryBox}>
          <Box className={classes.summaryIcon}>
            <Typography style={{ fontSize: '1.2rem' }}>📈</Typography>
          </Box>
          <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
            {getTranslation('Monthly Average', preferredLanguage)}
          </Typography>
          <Typography variant="h5" style={{ fontWeight: 700, color: 'white' }}>
            {summaryStats.monthlyAverage}
          </Typography>
        </Box>
        
        <Box className={classes.summaryBox}>
          <Box className={classes.summaryIcon}>
            <Typography style={{ fontSize: '1.2rem' }}>🚀</Typography>
          </Box>
          <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
            {getTranslation('Peak Month', preferredLanguage)}
          </Typography>
          <Typography variant="h5" style={{ fontWeight: 700, color: 'white' }}>
            {summaryStats.peakMonth}
          </Typography>
        </Box>
        
        <Box className={classes.summaryBox}>
          <Box className={classes.summaryIcon}>
            <Typography style={{ fontSize: '1.2rem' }}>📚</Typography>
          </Box>
          <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
            {getTranslation('Active Modules', preferredLanguage)}
          </Typography>
          <Typography variant="h5" style={{ fontWeight: 700, color: 'white' }}>
            {summaryStats.activeModules}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default TrainingProgressTimeline;